import { makeConfigApiCreator } from "../../creators";
import { ParseFunction } from "../../creators/AjaxCreator";

/**
 * Shared plumbing for talking to the Izuma edge-k8s endpoint.
 *
 * The endpoint speaks the standard Kubernetes API, so every view here is a
 * plain GET against `edge_k8s_base`. Two concerns are common to all of them and
 * live here rather than being repeated per resource.
 */

/**
 * Local development escape hatch. The portal normally authenticates with the
 * signed-in user's bearer token, which is what ships. If edge_k8s_token is set
 * in conf/settings.js we send that instead - useful for pointing a local dev
 * server at a cluster an ordinary portal login cannot reach. It is never set in
 * a deployed configuration.
 */
const devToken = (): string | undefined => {
    const config = window.appConfig as { edge_k8s_token?: string } | undefined;

    return config ? config.edge_k8s_token : undefined;
};

/**
 * Build a Kubernetes creator whose base URL is the edge-k8s endpoint.
 *
 * `D` is the type of the request body, which only the creators that send one
 * (POST, PATCH) need to name.
 */
export const makeK8sCreator = <A, D = undefined>(type: string, parse: ParseFunction<A, D>) =>
    makeConfigApiCreator<A, D>("edge_k8s_base", { errorCallout: true })(type, parse);

/**
 * Options shared by every Kubernetes request.
 *
 * `errorRedirect: false` matters: a 401 from this endpoint means the account
 * cannot reach the cluster, not that the portal session expired, so it must
 * never log the user out of the portal.
 */
export interface K8sRequest {
    param: string;
    queryParameters?: { [key: string]: string };
    headers?: { [header: string]: string };
    useAuth?: boolean;
    errorRedirect: boolean;
}

export const k8sRequest = (param: string, queryParameters?: { [key: string]: string }): K8sRequest => {
    const token = devToken();

    return {
        param,
        ...(queryParameters ? { queryParameters } : {}),
        ...(token ? { headers: { Authorization: `Bearer ${token}` }, useAuth: false } : {}),
        errorRedirect: false,
    };
};
